/**
 * Whether the signed-in user's company workspace is closed (SC-409).
 *
 * `GET /api/company/me/access` is the single source of truth for the suspended-company
 * gate. It applies the same block rule as the server's room join check, the company lobby
 * route and the chat token, so the client no longer guesses "disabled" from error text.
 *
 * This module deliberately imports nothing that leads back into the stores or `parseCloud`:
 * `parseCloud`, `RoomHandler` and the Matrix client all report refusals through it, and a
 * cycle through any of them would be a TDZ hazard with the unminified Vite build.
 */
import { getServerBaseUrl } from '../utils/serverUrl'
import { getStoredSessionToken } from '../utils/sessionToken'

export type CompanyBlockReason = 'suspended' | 'deleted'

export interface CompanyAccess {
  companyId: string | null
  companyName: string | null
  role: 'manager' | 'admin' | 'user' | null
  blocked: boolean
  /** A deletion in progress is reported as 'suspended', exactly like the server rule. */
  reason: CompanyBlockReason | null
}

/**
 * Hand copy of the server's `COMPANY_JOIN_BLOCKED_MESSAGES`
 * (`server/services/companyAccessBlock.ts`). The cloud booking guards throw the same text
 * with Parse error 119. Matching is exact on purpose: other 403s must keep their meaning.
 */
export const COMPANY_BLOCKED_MESSAGES: Record<CompanyBlockReason, string> = {
  suspended: 'This company workspace is suspended. Contact your administrator.',
  deleted: 'This company workspace no longer exists.',
}

/** `code` values of the server's `COMPANY_BLOCKED_HTTP` refusals (403 and 410). */
export const COMPANY_BLOCKED_HTTP_CODES = ['COMPANY_INACTIVE', 'COMPANY_DELETED'] as const

/** Parse.Error.OPERATION_FORBIDDEN, which the cloud guards use for a blocked company. */
const PARSE_OPERATION_FORBIDDEN = 119

/** A hung request must not leave the gate "unknown" forever; failing lets the UI fail open. */
export const COMPANY_ACCESS_TIMEOUT_MS = 10_000

const ACCESS_ROLES = new Set(['manager', 'admin', 'user'])

export function isCompanyBlockedMessage(message: unknown): boolean {
  return (
    typeof message === 'string' &&
    (message === COMPANY_BLOCKED_MESSAGES.suspended || message === COMPANY_BLOCKED_MESSAGES.deleted)
  )
}

export function isCompanyBlockedCloudError(error: unknown): boolean {
  const e = error as { code?: unknown; message?: unknown } | null | undefined
  return e?.code === PARSE_OPERATION_FORBIDDEN && isCompanyBlockedMessage(e?.message)
}

export function isCompanyBlockedHttpCode(code: unknown): boolean {
  return (
    typeof code === 'string' && (COMPANY_BLOCKED_HTTP_CODES as readonly string[]).includes(code)
  )
}

/**
 * Thrown by `RoomHandler` instead of its generic "Invalid password" rewrite, so a join
 * refused because the company is closed never reopens the password dialog.
 */
export class CompanyBlockedJoinError extends Error {
  readonly companyBlocked = true

  constructor(message: string) {
    super(message)
    this.name = 'CompanyBlockedJoinError'
  }
}

export function isCompanyBlockedJoinError(error: unknown): error is CompanyBlockedJoinError {
  return (
    error instanceof CompanyBlockedJoinError ||
    (error as { companyBlocked?: unknown } | null)?.companyBlocked === true
  )
}

let refusalHandler: (() => void) | null = null

/**
 * The access store registers itself here when it loads. Callers that see a refusal only
 * signal; they never import the store, which keeps this module free of import cycles.
 */
export function registerCompanyAccessRefusalHandler(handler: (() => void) | null): void {
  refusalHandler = handler
}

/** A backend refused something because the company is closed: re-check access now. */
export function signalCompanyAccessRefusal(): void {
  try {
    refusalHandler?.()
  } catch (error) {
    console.warn('[CompanyAccess] Refusal handler failed', error)
  }
}

function parseAccess(body: any): CompanyAccess {
  if (!body || body.success !== true || typeof body.blocked !== 'boolean') {
    throw new Error('Malformed company access response')
  }
  const reason = body.reason === 'suspended' || body.reason === 'deleted' ? body.reason : null
  return {
    companyId: typeof body.companyId === 'string' ? body.companyId : null,
    companyName: typeof body.companyName === 'string' ? body.companyName : null,
    role: ACCESS_ROLES.has(body.role) ? body.role : null,
    blocked: body.blocked,
    // A blocked answer always carries a reason; default it so the copy never breaks.
    reason: body.blocked ? reason ?? 'suspended' : null,
  }
}

/**
 * Throws on any failure (network, timeout, non-2xx, malformed body). Callers keep their last
 * known state instead: the UI fails open and the backend still enforces the closure.
 *
 * `sessionToken` overrides the stored token, for the invitation page, which has just minted
 * a session that may not be in local storage yet.
 */
export async function fetchCompanyAccess(
  opts: { sessionToken?: string } = {}
): Promise<CompanyAccess> {
  const token = opts.sessionToken || getStoredSessionToken()
  if (!token) throw new Error('Not signed in')

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), COMPANY_ACCESS_TIMEOUT_MS) : null
  try {
    const response = await fetch(`${getServerBaseUrl()}/api/company/me/access`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      ...(controller ? { signal: controller.signal } : {}),
    })
    if (!response.ok) {
      throw new Error(`Company access check failed (HTTP ${response.status})`)
    }
    return parseAccess(await response.json())
  } finally {
    if (timer) clearTimeout(timer)
  }
}
